
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
// Image removed - use img tag;
import { Link } from "react-router-dom";
import {
  Heart,
  ShoppingBag,
  Share2,
  Facebook,
  Twitter,
  Image as ImageIcon,
  Minus,
  Plus,
  Truck,
  Shield,
  RotateCcw,
  Star,
  Check,
  ArrowLeft,
  Rocket,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useCartStore, useWishlistStore, useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { productsApi, collectionsApi, customerReviewsApi } from "@/lib/api";
import { getSmartProductPlaceholder } from "@/lib/placeholders";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";
import ImageGallery, { useImageGallery } from "../components/common/ImageGallery";
import { Clock, Calendar, X } from "lucide-react";

interface ProductImage {
  id: number;
  image_url: string;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
}

interface ProductVariant {
  id: number;
  product_id: number;
  color: string;
  size: string | null;
  sku: string | null;
  price: number | null;
  stock: number;
  image_url: string | null;
  is_active: boolean;
  display_order: number;
}

interface Product {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  original_price: number | null;
  buyoutprice: number | null;
  image_url: string | null;
  stock: number;
  order_count: number;
  collection_id: number | null;
  category_id: number | null;
  is_active: boolean;
  is_featured: boolean;
  dimensions: string | null;
  care_tips: string | null;
  shipping_info: string | null;
  style_tip: string | null;
  tax_included: boolean;
  cod_available: boolean;
  returns_available: boolean;
  express_delivery_available: boolean;
  free_shipping_threshold: number | null;
  quality_description: string | null;
  created_at: string;
  updated_at: string;
  images?: ProductImage[];
  variants?: ProductVariant[];
}

export default function ProductDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const slug = params.slug as string;
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [similarProducts, setSimilarProducts] = useState<any[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [collectionName, setCollectionName] = useState<string | null>(null);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const [productReviews, setProductReviews] = useState<any[]>([]);
  const [showReviewsDialog, setShowReviewsDialog] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const { isOpen, initialIndex, openGallery, closeGallery } = useImageGallery();
  const [galleryImages, setGalleryImages] = useState<Array<{ id: number | string; url: string; alt?: string; thumbnail?: string }>>([]);

  const { addItem: addToCart } = useCartStore();
  const { addItem: addToWishlist, isInWishlist, removeItem: removeFromWishlist, addItemToBackend, removeItemFromBackend } = useWishlistStore();
  const { isAuthenticated, token } = useAuthStore();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Track navigation - update previous route so ProductsPage knows we came from product detail
  useEffect(() => {
    try {
      sessionStorage.setItem("aarsa_previous_route", location.pathname);
    } catch (error) {
      // Ignore storage errors
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await productsApi.getBySlug(slug);
        setProduct(data);
        
        // Fetch collection name if collection_id exists
        if (data.collection_id) {
          try {
            const collections = await collectionsApi.getAll();
            const collection = Array.isArray(collections) 
              ? collections.find((c: any) => c.id === data.collection_id)
              : (collections.collections || []).find((c: any) => c.id === data.collection_id);
            if (collection) {
              setCollectionName(collection.title || collection.name);
            }
          } catch (error) {
            console.error("Error fetching collection:", error);
          }
        }

        // Fetch review count for the product
        if (data.name) {
          try {
            const countData = await customerReviewsApi.getCountByProductName(data.name);
            setReviewCount(countData.count || 0);
          } catch (error) {
            console.error("Error fetching review count:", error);
            setReviewCount(0);
          }
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  const handleReviewsClick = async () => {
    if (!product) return;
    
    setShowReviewsDialog(true);
    setLoadingReviews(true);
    
    try {
      const reviewsData = await customerReviewsApi.getByProductName(product.name, 5);
      setProductReviews(reviewsData.reviews || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to load reviews");
      setProductReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  // Auto-select first variant when product loads
  useEffect(() => {
    if (product?.variants && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      setSelectedColor(firstVariant.color);
      setSelectedSize(firstVariant.size || null);
    }
  }, [product]);

  // Reset image index when variant changes (to show variant image)
  useEffect(() => {
    if (product?.variants) {
      const activeVariants = product.variants.filter((v: any) => v.is_active) || [];
      const selectedVariant = activeVariants.find(
        (v: any) => v.color === selectedColor && (v.size === selectedSize || (!v.size && !selectedSize))
      );
      if (selectedVariant?.image_url) {
        setSelectedImageIndex(0);
      }
    }
  }, [product, selectedColor, selectedSize]);

  useEffect(() => {
    const fetchSimilarProducts = async () => {
      if (!slug) return;
      
      try {
        setLoadingSimilar(true);
        const data = await productsApi.getSimilar(slug, 8);
        setSimilarProducts(data.products || []);
      } catch (error) {
        console.error("Error fetching similar products:", error);
        // Don't show error toast for similar products, just fail silently
      } finally {
        setLoadingSimilar(false);
      }
    };

    if (slug && product) {
      fetchSimilarProducts();
    }
  }, [slug, product]);

  const handleAddToCart = () => {
    if (!product) return;
    
    // Find selected variant based on color and size
    const selectedVariant = product.variants?.find(
      (v) => v.is_active && v.color === selectedColor && (v.size === selectedSize || (!v.size && !selectedSize))
    );
    
    // Use variant price if available, otherwise product price
    const price = selectedVariant?.price ?? product.price;
    const stock = selectedVariant?.stock ?? product.stock;
    
    if (stock < quantity) {
      toast.error(`Only ${stock} item(s) available in stock`);
      return;
    }
    
    // Get variant image if available, otherwise primary image
    const imageUrl = selectedVariant?.image_url || 
                     product.images?.find((img: any) => img.is_primary)?.image_url || 
                     product.image_url || 
                     getSmartProductPlaceholder(product.name);
    
    // Create unique ID based on product and variant
    const itemId = selectedVariant 
      ? `${product.id}-${selectedVariant.id}`
      : `${product.id}`;
    
    addToCart({
      id: itemId,
      productId: product.id,
      variantId: selectedVariant?.id,
      slug: product.slug,
      name: product.name,
      price: price,
      originalPrice: product.original_price || undefined,
      image: imageUrl,
      quantity,
      sku: selectedVariant?.sku || undefined,
    });
    toast.success("Added to cart!");
  };

  const handleBuyNow = () => {
    if (!product) return;
    
    // Find selected variant based on color and size
    const selectedVariant = product.variants?.find(
      (v) => v.is_active && v.color === selectedColor && (v.size === selectedSize || (!v.size && !selectedSize))
    );
    
    // Use variant price if available, otherwise product price
    const price = selectedVariant?.price ?? product.price;
    const stock = selectedVariant?.stock ?? product.stock;
    
    if (stock < quantity) {
      toast.error(`Only ${stock} item(s) available in stock`);
      return;
    }
    
    // Get variant image if available, otherwise primary image
    const imageUrl = selectedVariant?.image_url || 
                     product.images?.find((img: any) => img.is_primary)?.image_url || 
                     product.image_url || 
                     getSmartProductPlaceholder(product.name);
    
    // Create unique ID based on product and variant
    const itemId = selectedVariant 
      ? `${product.id}-${selectedVariant.id}`
      : `${product.id}`;
    
    // Add to cart
    addToCart({
      id: itemId,
      productId: product.id,
      variantId: selectedVariant?.id,
      slug: product.slug,
      name: product.name,
      price: price,
      originalPrice: product.original_price || undefined,
      image: imageUrl,
      quantity,
      sku: selectedVariant?.sku || undefined,
    });
    
    // Navigate to cart page
    navigate("/cart");
  };

  const handleSimilarProductWishlistToggle = async (similarProduct: any) => {
    if (!isAuthenticated || !token) {
      setShowLoginDialog(true);
      return;
    }

    const isInWishlistNow = isInWishlist(similarProduct.id);
    
    if (isInWishlistNow) {
      // Remove from wishlist
      const success = await removeItemFromBackend(similarProduct.id, token);
      if (success) {
        removeFromWishlist(similarProduct.id);
        toast.success("Removed from wishlist");
      } else {
        toast.error("Failed to remove from wishlist");
      }
    } else {
      // Add to wishlist
      const primaryImage = similarProduct.images?.find((img: any) => img.is_primary);
      const imageUrl = primaryImage?.image_url || similarProduct.image_url || getSmartProductPlaceholder(similarProduct.name);
      
      const success = await addItemToBackend(similarProduct.id, token);
      if (success) {
        addToWishlist({
          id: similarProduct.id,
          name: similarProduct.name,
          price: similarProduct.price,
          originalPrice: similarProduct.original_price || undefined,
          image: imageUrl,
          slug: similarProduct.slug,
        });
        toast.success("Added to wishlist!");
      } else {
        toast.error("Failed to add to wishlist");
      }
    }
  };

  const handleWishlistToggle = async () => {
    if (!product) return;
    
    // Check if user is authenticated
    if (!isAuthenticated || !token) {
      setShowLoginDialog(true);
      return;
    }
    
    try {
      if (isInWishlist(product.id)) {
        // Remove from wishlist
        const success = await removeItemFromBackend(product.id, token);
        if (success) {
          removeFromWishlist(product.id);
          toast.success("Removed from wishlist");
        } else {
          toast.error("Failed to remove from wishlist");
        }
      } else {
        // Add to wishlist
        const success = await addItemToBackend(product.id, token);
        if (success) {
          addToWishlist({
            id: product.id,
            name: product.name,
            price: product.price,
            originalPrice: product.original_price || undefined,
            image: product.image_url || getSmartProductPlaceholder(product.name),
            slug: product.slug,
          });
          toast.success("Added to wishlist!");
        } else {
          toast.error("Failed to add to wishlist");
        }
      }
    } catch (error) {
      console.error("Wishlist toggle error:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleShare = (platform: string) => {
    if (!product) return;
    const url = window.location.href;
    const text = product.name;
    const imageUrl = product.image_url || "";
    let shareUrl = "";

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case "pinterest":
        shareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(text)}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400");
    }
  };

  // Calculate discount
  const calculateDiscount = (price: number, originalPrice: number | null) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-grow pt-24 pb-24">
          <div className="container mx-auto px-4 py-12">
            {/* Breadcrumb Skeleton */}
            <div className="mb-6">
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Image Gallery Skeleton */}
              <div>
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-200 animate-pulse mb-4" />
                <div className="grid grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="relative aspect-square rounded-lg bg-gray-200 animate-pulse" />
                  ))}
                </div>
              </div>

              {/* Product Info Skeleton */}
              <div className="space-y-6">
                <div className="h-10 w-3/4 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-1/2 bg-gray-200 rounded animate-pulse" />
                <div className="h-6 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-6 w-2/3 bg-gray-200 rounded animate-pulse" />
                <div className="h-12 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-12 w-full bg-gray-200 rounded animate-pulse" />
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-5 w-full bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-grow pt-24 pb-24">
          <div className="container mx-auto px-4 py-12 text-center">
            <h1 className="text-3xl font-bold mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Products</span>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const discount = calculateDiscount(product.price, product.original_price);
  
  // Get active variants
  const activeVariants = product.variants?.filter(v => v.is_active) || [];
  
  // Get available colors and sizes from variants
  const availableColors = [...new Set(activeVariants.map(v => v.color))];
  const availableSizes = [...new Set(activeVariants.filter(v => v.size).map(v => v.size!))];
  
  // Find selected variant based on color and size
  const selectedVariant = activeVariants.find(
    (v) => v.color === selectedColor && (v.size === selectedSize || (!v.size && !selectedSize))
  );
  
  // Use variant stock/price if variant selected, otherwise product stock/price
  const currentPrice = selectedVariant?.price ?? product.price;
  const currentStock = selectedVariant?.stock ?? product.stock;
  const isOutOfStock = currentStock === 0;
  
  // Get all product images, sorted by display_order, with primary first
  const productImages = product.images && product.images.length > 0
    ? [...product.images].sort((a, b) => {
        // Primary image first, then by display_order
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        return a.display_order - b.display_order;
      })
    : [];
  
  // If selected variant has an image, prioritize it; otherwise use product images
  const variantImage = selectedVariant?.image_url;
  const allImages = variantImage
    ? [variantImage, ...productImages.map(img => img.image_url).filter(img => img !== variantImage)]
    : (productImages.length > 0
        ? productImages.map(img => img.image_url)
        : [product.image_url || getSmartProductPlaceholder(product.name)]);
  
  const currentImage = allImages[selectedImageIndex] || allImages[0];

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow pt-24 pb-32 md:pb-24">
        <div className="container mx-auto px-4 py-12">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Products</span>
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left: Vertical Thumbnails */}
            {allImages.length > 1 && (
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="hidden lg:block lg:col-span-1"
              >
                <div className="flex flex-col gap-3 sticky top-24">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index
                          ? "border-gray-900 shadow-md"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = getSmartProductPlaceholder(product.name);
                        }}
                      />
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Center: Main Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className={allImages.length > 1 ? "lg:col-span-5" : "lg:col-span-6"}
            >
              <div 
                className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 shadow-lg cursor-pointer hover:opacity-95 transition-opacity"
                onClick={() => {
                  const images = allImages.map((imgUrl, index) => ({
                    id: index,
                    url: imgUrl,
                    alt: productImages[index]?.alt_text || product.name,
                    thumbnail: imgUrl,
                  }));
                  
                  setGalleryImages(images);
                  openGallery(selectedImageIndex);
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${currentImage}-${selectedColor}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <img
                      src={currentImage}
                      alt={productImages[selectedImageIndex]?.alt_text || product.name}
                      className="object-contain max-w-full max-h-full"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = getSmartProductPlaceholder(product.name);
                      }}
                    />
                  </motion.div>
                </AnimatePresence>
                
                {/* Discount Badge */}
                {discount > 0 && (
                  <div className="absolute top-2 left-2 z-10">
                    <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-gray-900 text-white shadow-sm">
                      {discount}% OFF
                    </span>
                  </div>
                )}
                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                    <span className="px-4 py-2 bg-gray-900 text-white rounded text-sm font-medium">
                      Out of Stock
                    </span>
                  </div>
                )}
              </div>
              
              {/* Mobile Thumbnails */}
              {allImages.length > 1 && (
                <div className="grid grid-cols-4 gap-3 mt-4 lg:hidden">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index
                          ? "border-gray-900"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = getSmartProductPlaceholder(product.name);
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`space-y-5 ${allImages.length > 1 ? "lg:col-span-6" : "lg:col-span-6"}`}
            >
              {/* Brand Name */}
              {collectionName && (
                <p className="text-sm text-gray-500 font-medium">{collectionName}</p>
              )}

              {/* Product Name */}
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-center gap-3">
                <div className="text-2xl md:text-3xl font-semibold text-[#87AE73]">
                  Rs. {product.price.toLocaleString()}.00
                </div>
                {product.original_price && discount > 0 && (
                  <div className="text-sm text-gray-500">
                    <span className="line-through">Rs. {product.original_price.toLocaleString()}.00</span>
                  </div>
                )}
              </div>

              {/* Star Rating or New Arrival */}
              {reviewCount > 0 ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {/* 4 filled stars */}
                    {[1, 2, 3, 4].map((star) => (
                      <Star
                        key={star}
                        className="w-4 h-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                    {/* Half-filled star (5th star) */}
                    <div className="relative w-4 h-4 inline-block">
                      <Star className="w-4 h-4 text-gray-300" />
                      <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleReviewsClick}
                    className="text-sm text-gray-600 hover:text-gray-900 underline cursor-pointer"
                  >
                    {reviewCount} reviews
                  </button>
                </div>
              ) : (
                <div className="flex items-center">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    New Arrival
                  </span>
                </div>
              )}

              {/* Flash Sale Banner */}
              {discount > 0 && (
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Flat {discount}% OFF + Extra 15% OFF</p>
                      <p className="text-xs text-gray-600">Sitewide Flash Sale + Free Shipping</p>
                    </div>
                    <span className="px-2 py-1 bg-orange-500 text-white rounded text-xs font-bold">FLASH SALE</span>
                  </div>
                  <p className="text-xs text-gray-600">Limited Time Only</p>
                </div>
              )}

              {/* Free Gift Offer Banner */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Shop for ₹3,000 & get a Free Gift</p>
                    <p className="text-xs text-gray-600">Choose your favourite jewellery now</p>
                  </div>
                  <span className="px-2 py-1 bg-purple-500 text-white rounded text-xs font-bold">FREE GIFT</span>
                </div>
                <p className="text-xs text-gray-600">Offer for limited time!</p>
              </div>

              {/* Color Selector */}
              {availableColors.length > 0 && (
                <div>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map((color) => {
                      const isSelected = selectedColor === color;
                      const variantForColor = activeVariants.find(v => v.color === color);
                      const isOutOfStockForColor = (variantForColor?.stock ?? 0) === 0;
                      
                      return (
                        <motion.button
                          key={color}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedColor(color);
                            const sizesForColor = activeVariants
                              .filter(v => v.color === color && v.size)
                              .map(v => v.size!);
                            if (sizesForColor.length > 0 && !sizesForColor.includes(selectedSize || '')) {
                              setSelectedSize(sizesForColor[0]);
                            } else if (sizesForColor.length === 0) {
                              setSelectedSize(null);
                            }
                          }}
                          disabled={isOutOfStockForColor}
                          className={`px-4 py-2 rounded border text-sm font-medium transition-all ${
                            isSelected
                              ? "border-gray-900 bg-gray-900 text-white"
                              : "border-gray-300 hover:border-gray-400 text-gray-700 bg-white"
                          } ${
                            isOutOfStockForColor
                              ? "opacity-50 cursor-not-allowed line-through"
                              : ""
                          }`}
                        >
                          {color}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Size Selector */}
              {availableSizes.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Size {selectedSize && `(${selectedSize})`}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map((size) => {
                      const isSelected = selectedSize === size;
                      const variantForSize = activeVariants.find(
                        v => v.color === selectedColor && v.size === size
                      );
                      const isOutOfStockForSize = (variantForSize?.stock ?? 0) === 0;
                      
                      return (
                        <motion.button
                          key={size}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSelectedSize(size)}
                          disabled={isOutOfStockForSize}
                          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            isSelected
                              ? "border-gray-900 bg-gray-900 text-white"
                              : "border-gray-300 hover:border-gray-400 text-gray-700"
                          } ${
                            isOutOfStockForSize
                              ? "opacity-50 cursor-not-allowed line-through"
                              : ""
                          }`}
                        >
                          {size}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stock Status */}
              {!isOutOfStock && currentStock > 0 && currentStock <= 10 && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    Low stock - Only {currentStock} items left
                  </p>
                </div>
              )}

              {/* Quantity Selector */}
              <div>
                <div className="flex items-center border border-gray-300 rounded w-fit">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="p-2 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </motion.button>
                  <span className="px-4 py-2 text-base font-medium text-gray-900 border-x border-gray-300 min-w-[3rem] text-center">{quantity}</span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setQuantity(Math.min(currentStock || 10, quantity + 1))}
                    disabled={quantity >= (currentStock || 10)}
                    className="p-2 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </motion.button>
                </div>
              </div>

              {/* Shipping Info */}
              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5 text-sm text-gray-700">
                  <Truck className="w-4 h-4 text-gray-600 flex-shrink-0" />
                  <span>Free Shipping For All Prepaid order In India</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-gray-700">
                  <Clock className="w-4 h-4 text-gray-600 flex-shrink-0" />
                  <span>Order Dispatch {new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-gray-700">
                  <Calendar className="w-4 h-4 text-gray-600 flex-shrink-0" />
                  <span>Order Delivery in {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                >
                  <ShoppingBag className="w-4 h-4" />
                  <span>{isOutOfStock ? "Out of Stock" : "Add to Cart"}</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={handleBuyNow}
                  disabled={isOutOfStock}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#87AE73] text-white rounded-lg hover:bg-[#7A8B6F] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                >
                  <Rocket className="w-4 h-4" />
                  <span>Buy Now</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleWishlistToggle}
                  className={`p-3 rounded-lg border transition-all ${
                    isInWishlist(product.id)
                      ? "border-gray-400 bg-gray-100 text-gray-700"
                      : "border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-600"
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isInWishlist(product.id) ? "fill-current" : ""}`} />
                </motion.button>
              </div>

              {/* Social Share */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Share</p>
                <div className="flex flex-wrap gap-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleShare("facebook")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all text-xs text-gray-600"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-3.5 h-3.5" />
                    <span>Share on Facebook</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleShare("twitter")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all text-xs text-gray-600"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-3.5 h-3.5" />
                    <span>Tweet on X</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleShare("pinterest")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all text-xs text-gray-600"
                    aria-label="Share on Pinterest"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>Pin it</span>
                  </motion.button>
                </div>
              </div>

              {/* Accordions */}
              <div className="border-t border-gray-200 pt-5 space-y-0">
                {/* Description Accordion */}
                {product.description && (
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => {
                        const newOpen = new Set(openAccordions);
                        if (newOpen.has("description")) {
                          newOpen.delete("description");
                        } else {
                          newOpen.add("description");
                        }
                        setOpenAccordions(newOpen);
                      }}
                      className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-sm text-gray-900">Description</span>
                      <span className="text-gray-600 text-lg font-medium flex-shrink-0">
                        {openAccordions.has("description") ? "−" : "+"}
                      </span>
                    </button>
                    <AnimatePresence>
                      {openAccordions.has("description") && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-3 text-gray-600 text-xs leading-relaxed">
                            {product.description}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Dimensions Accordion */}
                <div className="border-b border-gray-200">
                  <button
                    onClick={() => {
                      const newOpen = new Set(openAccordions);
                      if (newOpen.has("dimensions")) {
                        newOpen.delete("dimensions");
                      } else {
                        newOpen.add("dimensions");
                      }
                      setOpenAccordions(newOpen);
                    }}
                    className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-sm text-gray-900">Dimensions</span>
                    <span className="text-gray-600 text-lg font-medium flex-shrink-0">
                      {openAccordions.has("dimensions") ? "−" : "+"}
                    </span>
                  </button>
                  <AnimatePresence>
                    {openAccordions.has("dimensions") && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-3 text-gray-600 space-y-3 text-xs">
                          {product.dimensions ? (
                            <div className="whitespace-pre-line">
                              {product.dimensions.split('\n').map((line, index) => (
                                <div key={index} className="mb-2">
                                  {line}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500">No dimensions information available.</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Care Tips Accordion */}
                <div className="border-b border-gray-200">
                  <button
                    onClick={() => {
                      const newOpen = new Set(openAccordions);
                      if (newOpen.has("care")) {
                        newOpen.delete("care");
                      } else {
                        newOpen.add("care");
                      }
                      setOpenAccordions(newOpen);
                    }}
                    className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-sm text-gray-900">Care Tips</span>
                    <span className="text-gray-600 text-lg font-medium flex-shrink-0">
                      {openAccordions.has("care") ? "−" : "+"}
                    </span>
                  </button>
                  <AnimatePresence>
                    {openAccordions.has("care") && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-3 text-gray-600 text-xs">
                          {product.care_tips ? (
                            <div className="whitespace-pre-line text-gray-500">
                              {product.care_tips.split('\n').map((line, index) => (
                                <div key={index} className="mb-2">
                                  {line}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500">No care tips available.</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Shipping Accordion */}
                <div className="border-b border-gray-200">
                  <button
                    onClick={() => {
                      const newOpen = new Set(openAccordions);
                      if (newOpen.has("shipping")) {
                        newOpen.delete("shipping");
                      } else {
                        newOpen.add("shipping");
                      }
                      setOpenAccordions(newOpen);
                    }}
                    className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-sm text-gray-900">Shipping</span>
                    <span className="text-gray-600 text-lg font-medium flex-shrink-0">
                      {openAccordions.has("shipping") ? "−" : "+"}
                    </span>
                  </button>
                  <AnimatePresence>
                    {openAccordions.has("shipping") && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-3 text-gray-600 text-xs">
                          {product.shipping_info ? (
                            <div className="whitespace-pre-line text-gray-500">
                              {product.shipping_info.split('\n').map((line, index) => (
                                <div key={index} className="mb-2">
                                  {line}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-2 text-gray-500">
                              {product.free_shipping_threshold && (
                                <p>Free shipping on prepaid orders above ₹{product.free_shipping_threshold.toLocaleString()}</p>
                              )}
                              {product.express_delivery_available && (
                                <p>Express 24-hour delivery available in Delhi, NCR (charges apply)</p>
                              )}
                              {product.cod_available && (
                                <p>COD available for orders above ₹{product.free_shipping_threshold || 500}</p>
                              )}
                              {product.returns_available && (
                                <p>Only 2 days return accepted</p>
                              )}
                              {!product.shipping_info && !product.free_shipping_threshold && !product.express_delivery_available && (
                                <p>No shipping information available.</p>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Similar Products Section */}
        {similarProducts.length > 0 && (
          <div className="container mx-auto px-4 py-12 max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-semibold font-playfair text-gray-900 mb-6">
                Similar Products
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {similarProducts.map((similarProduct: any, index) => {
                  const discount = similarProduct.original_price && similarProduct.price < similarProduct.original_price
                    ? Math.round(((similarProduct.original_price - similarProduct.price) / similarProduct.original_price) * 100)
                    : 0;
                  const primaryImage = similarProduct.images?.find((img: any) => img.is_primary);
                  const imageUrl = primaryImage?.image_url || similarProduct.image_url || getSmartProductPlaceholder(similarProduct.name);
                  
                  return (
                    <motion.div
                      key={similarProduct.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      className="group relative"
                    >
                      <Link to={`/products/${similarProduct.slug}`}>
                        <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                          {/* Product Image */}
                          <img
                            src={imageUrl}
                            alt={similarProduct.name}
                            className={`object-cover w-full h-full transition-opacity duration-300 ${
                              similarProduct.stock === 0 ? "opacity-50" : "opacity-100"
                            }`}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getSmartProductPlaceholder(similarProduct.name);
                            }}
                          />

                          {/* Sold Out Badge - Top Right */}
                          {similarProduct.stock === 0 && (
                            <div className="absolute top-2 right-2 z-20">
                              <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-black text-white shadow-sm">
                                Sold out
                              </span>
                            </div>
                          )}

                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex space-x-4">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
                                aria-label="Quick view"
                              >
                                <Eye className="w-5 h-5 text-gray-900" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSimilarProductWishlistToggle(similarProduct);
                                }}
                                className={`p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors ${
                                  isInWishlist(similarProduct.id) ? "text-red-500" : "text-gray-900"
                                }`}
                                aria-label="Add to wishlist"
                              >
                                <Heart className={`w-5 h-5 ${isInWishlist(similarProduct.id) ? "fill-current" : ""}`} />
                              </motion.button>
                            </div>
                          </div>
                        </div>

                        {/* Product Info */}
                        <div className="mt-4 space-y-2">
                          <h3 className="font-semibold text-sm text-gray-900 group-hover:text-gray-700 transition-colors line-clamp-2">
                            {similarProduct.name}
                          </h3>
                          <div className="flex items-center space-x-2 flex-wrap">
                            <span className="text-lg font-bold text-gray-900">
                              ₹{similarProduct.price.toLocaleString()}
                            </span>
                            {similarProduct.original_price && discount > 0 && (
                              <>
                                <span className="text-xs text-gray-500 line-through">
                                  ₹{similarProduct.original_price.toLocaleString()}
                                </span>
                                <span className="text-xs font-semibold text-green-600">
                                  Save {discount}%
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </main>
      
      {/* Fixed Bottom Buttons - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-lg">
        <div className="flex gap-3 p-4">
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
          >
            <ShoppingBag className="w-4 h-4" />
            <span>{isOutOfStock ? "Out of Stock" : "Add to Cart"}</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={handleBuyNow}
            disabled={isOutOfStock}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#87AE73] text-white rounded-lg hover:bg-[#7A8B6F] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
          >
            <Rocket className="w-4 h-4" />
            <span>Buy Now</span>
          </motion.button>
        </div>
      </div>
      
      {/* OTP Login Dialog */}
      <OTPLoginDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onSuccess={async () => {
          // After successful login, add to wishlist
          const authState = useAuthStore.getState();
          if (authState.token && product) {
            const success = await addItemToBackend(product.id, authState.token);
            if (success) {
              addToWishlist({
                id: product.id,
                name: product.name,
                price: product.price,
                originalPrice: product.original_price || undefined,
                image: product.image_url || getSmartProductPlaceholder(product.name),
                slug: product.slug,
              });
              toast.success("Added to wishlist!");
            }
          }
        }}
      />

      {/* Image Gallery */}
      {isOpen && galleryImages.length > 0 && (
        <ImageGallery
          images={galleryImages}
          initialIndex={initialIndex}
          isOpen={isOpen}
          onClose={closeGallery}
          showThumbnails={true}
        />
      )}

      {/* Reviews Dialog */}
      <AnimatePresence>
        {showReviewsDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReviewsDialog(false)}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Customer Reviews ({reviewCount || productReviews.length})
                </h2>
                <button
                  onClick={() => setShowReviewsDialog(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Reviews List */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingReviews ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Loading reviews...</p>
                  </div>
                ) : productReviews.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No reviews yet for this product.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {productReviews.map((review) => (
                      <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                        <div className="flex items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="font-semibold text-gray-900">{review.customer_name}</div>
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`w-4 h-4 ${
                                      star <= review.rating
                                        ? "fill-yellow-400 text-yellow-400"
                                        : "text-gray-300"
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                            {review.location && (
                              <p className="text-sm text-gray-500 mb-2">{review.location}</p>
                            )}
                            <p className="text-gray-700 leading-relaxed">{review.review_text}</p>
                            {review.created_at && (
                              <p className="text-xs text-gray-400 mt-2">
                                {new Date(review.created_at).toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
